import {
  logFormInteraction,
  logSearch,
  logUserInteraction,
} from "@/utils/logger";

/**
 * Utility for adding logging to form elements
 */
export class FormLogger {
  private formElement: HTMLFormElement;
  private formName: string;

  constructor(formElement: HTMLFormElement, formName: string) {
    this.formElement = formElement;
    this.formName = formName;
    this.setupFormLogging();
  }

  private setupFormLogging() {
    // Log form submission
    this.formElement.addEventListener("submit", () => {
      this.logFormSubmission();
    });

    // Log input focus/blur events
    const inputs = this.formElement.querySelectorAll("input, textarea, select");
    inputs.forEach((input) => {
      const inputElement = input as
        | HTMLInputElement
        | HTMLTextAreaElement
        | HTMLSelectElement;

      input.addEventListener("focus", () => {
        logFormInteraction(
          this.formName,
          "focus",
          inputElement.name || input.id,
        );
      });

      input.addEventListener("blur", () => {
        logFormInteraction(
          this.formName,
          "blur",
          inputElement.name || input.id,
        );
      });

      // Log changes to important fields
      if (
        inputElement instanceof HTMLInputElement &&
        (inputElement.type === "search" || inputElement.type === "text")
      ) {
        let timeoutId: NodeJS.Timeout;
        input.addEventListener("input", () => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            const value = inputElement.value;
            if (value.length > 2) {
              // Only log meaningful input
              logFormInteraction(
                this.formName,
                "input_change",
                inputElement.name || input.id,
              );
            }
          }, 500); // Debounce for 500ms
        });
      }
    });
  }

  private logFormSubmission() {
    const formData = new FormData(this.formElement);
    const data: Record<string, any> = {};

    // Convert FormData to object for logging
    formData.forEach((value, key) => {
      data[key] = value;
    });

    // Log form submission
    logFormInteraction(this.formName, "submit");

    // Special handling for search forms
    if (this.isSearchForm()) {
      const searchQuery = this.extractSearchQuery(data);
      if (searchQuery) {
        logSearch(searchQuery);
      }
    }

    // Log general form submission
    logUserInteraction("submit", this.formName, {
      fieldCount: Object.keys(data).length,
      hasRequiredFields: this.hasRequiredFields(),
    });
  }

  private isSearchForm(): boolean {
    return (
      this.formElement.action.includes("/sch") ||
      this.formElement.querySelector('input[type="search"]') !== null ||
      this.formName.toLowerCase().includes("search")
    );
  }

  private extractSearchQuery(formData: Record<string, any>): string | null {
    // Common search field names
    const searchFields = ["q", "query", "search", "_nkw", "keywords"];

    for (const field of searchFields) {
      if (formData[field] && typeof formData[field] === "string") {
        return formData[field].toString().trim();
      }
    }

    return null;
  }

  private hasRequiredFields(): boolean {
    return this.formElement.querySelectorAll("[required]").length > 0;
  }
}

/**
 * Auto-setup logging for all forms on the page
 */
export function setupAutoFormLogging() {
  if (typeof window === "undefined") return;

  // Wait for DOM to be ready
  const initializeForms = () => {
    const forms = document.querySelectorAll("form");
    forms.forEach((form, index) => {
      const formName = form.name || form.id || `form_${index}`;
      new FormLogger(form, formName);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeForms);
  } else {
    initializeForms();
  }

  // Also watch for dynamically added forms
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;

          // Check if the added node is a form
          if (element.tagName === "FORM") {
            const formName =
              (element as HTMLFormElement).name ||
              (element as HTMLFormElement).id ||
              `dynamic_form_${Date.now()}`;
            new FormLogger(element as HTMLFormElement, formName);
          }

          // Check if the added node contains forms
          const forms = element.querySelectorAll("form");
          forms.forEach((form, index) => {
            const formName =
              form.name || form.id || `dynamic_form_${Date.now()}_${index}`;
            new FormLogger(form, formName);
          });
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
